import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Crown, Zap, Shield, ArrowUpCircle, Clock, MessageSquare, Building2, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUserCompanyId } from '@/hooks/useSubscriptionFeatures';
import { UpgradeRequestDialog, TopupRequestDialog } from '@/components/admin/UpgradeRequestDialog';
import AIUsageBreakdown from '@/components/admin/AIUsageBreakdown';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  TIER_LIMITS, 
  TIER_NAMES, 
  SubscriptionTier, 
  formatCurrency,
} from '@/lib/subscriptionFeatures';

interface CompanyData {
  id: string;
  company_name: string;
  subscription_tier: string;
  ai_pool_limit: number;
  ai_pool_used: number;
  topup_conversations: number;
  max_sites: number;
  upgrade_requested_tier: string | null;
  upgrade_requested_at: string | null;
  topup_requested_amount: number | null;
  topup_requested_at: string | null;
  topup_count_this_month: number;
}

/**
 * CompanySubscriptionView - For company admins only
 * Shows their own company subscription status with request-only actions
 * No approve/reject, no direct modification
 */
const CompanySubscriptionView = () => {
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [siteCount, setSiteCount] = useState(0);
  const [workerCount, setWorkerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [isTopupDialogOpen, setIsTopupDialogOpen] = useState(false);
  const [isUsageBreakdownOpen, setIsUsageBreakdownOpen] = useState(false);
  const { toast } = useToast();
  const { companyId, isLoading: isCompanyLoading } = useUserCompanyId();

  useEffect(() => {
    if (companyId && !isCompanyLoading) {
      fetchCompanyData();
    }
  }, [companyId, isCompanyLoading]);

  const fetchCompanyData = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      // Fetch company data
      const { data: companyData, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(companyData);

      // Fetch site count
      const { count: sites } = await supabase
        .from('company_locations')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('is_active', true);
      setSiteCount(sites || 0);

      // Fetch worker count
      const { count: workers } = await supabase
        .from('worker_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);
      setWorkerCount(workers || 0);

    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'professional':
        return <Badge className="bg-primary text-primary-foreground"><Zap className="h-3 w-3 mr-1" />Professional</Badge>;
      case 'enterprise':
        return <Badge className="bg-success text-success-foreground"><Shield className="h-3 w-3 mr-1" />Enterprise</Badge>;
      case 'enterprise_plus':
        return <Badge className="bg-warning text-warning-foreground"><Crown className="h-3 w-3 mr-1" />Enterprise+</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const getTierFeatures = (tier: string) => {
    const limits = TIER_LIMITS[tier as SubscriptionTier] || TIER_LIMITS.free;
    return [
      { label: 'Sites', value: limits.maxSites === -1 ? 'Unlimited' : limits.maxSites },
      { label: 'AI Conversations/mo', value: limits.aiConversationsPerMonth === -1 ? 'Unlimited' : limits.aiConversationsPerMonth.toLocaleString() },
      { label: 'Workers', value: 'Unlimited' },
      { label: 'Documents', value: limits.knowledgeBaseDocs === -1 ? 'Unlimited' : limits.knowledgeBaseDocs },
    ];
  };

  if (isLoading || isCompanyLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-muted rounded mb-2" />
          <div className="h-4 w-64 bg-muted rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse h-16 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Company not found
        </CardContent>
      </Card>
    );
  }

  const tier = company.subscription_tier as SubscriptionTier || 'free';
  const aiLimit = company.ai_pool_limit || 100;
  const aiUsed = company.ai_pool_used || 0;
  const topupPool = company.topup_conversations || 0;
  const aiPercentage = aiLimit === -1 ? 0 : (aiUsed / aiLimit) * 100;
  const hasUpgradeRequest = !!company.upgrade_requested_tier;
  const hasTopupRequest = !!company.topup_requested_amount;
  const maxTopupsPerMonth = tier === 'free' ? 3 : -1;
  const canRequestTopup = maxTopupsPerMonth === -1 || (company.topup_count_this_month || 0) < maxTopupsPerMonth;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">My Subscription</h2>
        <p className="text-muted-foreground">View your subscription plan and request upgrades</p>
      </div>

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-3">
                {company.company_name}
                {getTierBadge(tier)}
              </CardTitle>
              <CardDescription>
                {TIER_NAMES[tier]} Plan
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {hasUpgradeRequest ? (
                <Badge variant="outline" className="border-warning text-warning">
                  <Clock className="h-3 w-3 mr-1" />
                  Upgrade Pending
                </Badge>
              ) : tier !== 'enterprise_plus' && (
                <Button 
                  variant="outline" 
                  onClick={() => setIsUpgradeDialogOpen(true)}
                >
                  <ArrowUpCircle className="h-4 w-4 mr-2" />
                  Request Upgrade
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {getTierFeatures(tier).map((feature, i) => (
              <div key={i} className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold">{feature.value}</p>
                <p className="text-sm text-muted-foreground">{feature.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Sites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {siteCount} / {company.max_sites === -1 ? '∞' : company.max_sites}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Workers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workerCount}</div>
            <p className="text-xs text-muted-foreground">Unlimited</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              AI Conversations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {aiLimit === -1 ? (
              <div className="text-2xl font-bold">Unlimited</div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold">{aiUsed.toLocaleString()}</span>
                  <span className="text-sm text-muted-foreground">
                    / {aiLimit.toLocaleString()}{topupPool > 0 ? ` (+${topupPool})` : ''}
                  </span>
                </div>
                <Progress 
                  value={aiPercentage} 
                  className={`h-2 ${aiPercentage >= 80 ? '[&>div]:bg-warning' : ''}`} 
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top-up Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Conversation Top-ups
          </CardTitle>
          <CardDescription>
            Purchase additional AI conversations for your team
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">
                <span className="font-medium">Current top-up pool:</span>{' '}
                {topupPool > 0 ? `${topupPool} conversations` : 'No active top-ups'}
              </p>
              {tier === 'free' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Free tier: {company.topup_count_this_month || 0} / {maxTopupsPerMonth} top-ups used this month
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {hasTopupRequest ? (
                <Badge variant="outline" className="border-warning text-warning">
                  <Clock className="h-3 w-3 mr-1" />
                  Top-up Pending ({company.topup_requested_amount} conv.)
                </Badge>
              ) : canRequestTopup ? (
                <Button 
                  variant="outline" 
                  onClick={() => setIsTopupDialogOpen(true)}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Request Top-up
                </Button>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Monthly limit reached
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Usage Breakdown - Collapsible */}
      <Collapsible open={isUsageBreakdownOpen} onOpenChange={setIsUsageBreakdownOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Riwayat Penggunaan AI
                  </CardTitle>
                  <CardDescription>
                    Lihat detail penggunaan AI per fitur dan pengguna
                  </CardDescription>
                </div>
                {isUsageBreakdownOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <AIUsageBreakdown />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Pending Requests Info */}
      {(hasUpgradeRequest || hasTopupRequest) && (
        <Card className="border-warning/50 bg-warning/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              <Clock className="h-5 w-5" />
              Pending Requests
            </CardTitle>
            <CardDescription>
              Your requests are being reviewed by our team
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {hasUpgradeRequest && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-background">
                <div className="flex items-center gap-3">
                  <ArrowUpCircle className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Upgrade to {TIER_NAMES[company.upgrade_requested_tier as SubscriptionTier]}</p>
                    <p className="text-xs text-muted-foreground">
                      Requested on {new Date(company.upgrade_requested_at!).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-warning text-warning">Pending</Badge>
              </div>
            )}
            {hasTopupRequest && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-background">
                <div className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-warning" />
                  <div>
                    <p className="font-medium">{company.topup_requested_amount} AI Conversations</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(company.topup_requested_amount! * 2000)} • Requested on {new Date(company.topup_requested_at!).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-warning text-warning">Pending</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <UpgradeRequestDialog
        open={isUpgradeDialogOpen}
        onOpenChange={setIsUpgradeDialogOpen}
        companyId={companyId || undefined}
        currentTier={tier}
      />

      <TopupRequestDialog
        open={isTopupDialogOpen}
        onOpenChange={(open) => {
          setIsTopupDialogOpen(open);
          if (!open) fetchCompanyData(); // Refresh on close
        }}
        companyId={companyId || undefined}
      />
    </div>
  );
};

export default CompanySubscriptionView;